import React, { useState, useMemo } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  IconButton,
  VStack,
  Select,
  Box,
} from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/hooks';
import { FiFilter } from 'react-icons/fi';
import PropTypes from 'prop-types';
import StyledButtonBase from './StyledButtonBase';

const FilterIconDrawer = ({
  data,
  columns, // Receive the columns configuration
  onApplyFilters,
  placement = 'right',
  drawerWidth = '300px',
  buttonSize = 'lg',
  filterDrawerBg = 'white',
  iconColor = 'black',
  filterIconColor = 'black',
  filterdropdownBg = 'white',
  filterdropdownTextColor = 'black',
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFilters, setSelectedFilters] = useState({});

  // Map column IDs to their titles using columns configuration
  const idToTitleMap = useMemo(
    () =>
      columns.reduce((acc, col) => {
        acc[col.id] = col.title;
        return acc;
      }, {}),
    [columns]
  );

  // Extract unique values for each column to use in dropdowns, memoized for performance
  const columnValues = useMemo(
    () =>
      data.reduce((acc, row) => {
        Object.entries(row).forEach(([key, value]) => {
          if (!acc[key]) acc[key] = new Set();
          acc[key].add(value);
        });
        return acc;
      }, {}),
    [data]
  );

  const handleFilterChange = (column, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [column]: value,
    }));
  };

  const applyFilters = () => {
    onApplyFilters(selectedFilters);
    onClose();
  };

  const resetFilters = () => {
    setSelectedFilters({});
  };

  return (
    <>
      <IconButton
        icon={<FiFilter color={filterIconColor} />}
        onClick={onOpen}
        aria-label="Open filter drawer"
        variant="ghost"
        size={buttonSize}
        color={iconColor}
      />

      <Drawer isOpen={isOpen} placement={placement} onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg={filterDrawerBg} maxWidth={drawerWidth}>
          <DrawerCloseButton />
          <DrawerHeader>Filter Items</DrawerHeader>

          <DrawerBody>
            <VStack align="start" spacing={4}>
              {Object.keys(columnValues).map((columnId) => (
                <Box key={columnId} width="100%">
                  <Select
                    bg={filterdropdownBg}
                    color={filterdropdownTextColor}
                    placeholder={ idToTitleMap[columnId] || columnId} // Use the title from the map
                    onChange={(e) => handleFilterChange(columnId, e.target.value)}
                    value={selectedFilters[columnId] || ''}
                    _focus={{ bg: filterdropdownBg }}
                    _hover={{ bg: filterdropdownBg }}
                    border={0}
                    sx={{
                      option: {
                        backgroundColor: filterdropdownBg,
                        color: filterdropdownTextColor,
                      },
                    }}
                  >
                    {[...columnValues[columnId]].map((value) => (
                      <option
                        key={value}
                        value={value}
                        style={{
                          backgroundColor: filterdropdownBg,
                          color: filterdropdownTextColor,
                        }}
                      >
                        {value}
                      </option>
                    ))}
                  </Select>
                </Box>
              ))}
            </VStack>

            <Box display="flex" justifyContent="space-between" mt={4}>
              <StyledButtonBase
                bg="#808080"
                color="white"
                borderColor="#808080"
                onClick={resetFilters}
                size="md"
              >
                Reset Filters
              </StyledButtonBase>
              <StyledButtonBase
                bg="#00853E"
                color="white"
                borderColor="#00853E"
                onClick={applyFilters}
                size="md"
              >
                Apply Filters
              </StyledButtonBase>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

FilterIconDrawer.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired, // Added columns prop type
  onApplyFilters: PropTypes.func.isRequired,
  placement: PropTypes.oneOf(['left', 'right']),
  drawerWidth: PropTypes.string,
  buttonSize: PropTypes.oneOf(['sm', 'md', 'lg']),
  filterDrawerBg: PropTypes.string,
  iconColor: PropTypes.string,
  filterIconColor: PropTypes.string,
  filterdropdownBg: PropTypes.string,
  filterdropdownTextColor: PropTypes.string,
};

FilterIconDrawer.defaultProps = {
  placement: 'right',
  drawerWidth: '300px',
  buttonSize: 'lg',
  filterDrawerBg: 'white',
  iconColor: 'black',
  filterIconColor: 'black',
  filterdropdownBg: 'white',
  filterdropdownTextColor: 'black',
};

export default FilterIconDrawer;
